import { ArrowRight, Loader2, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicHeader } from '@/components/PublicHeader';
import { Particles } from '@/components/ui/particles/Particles';
import {
  SpecularButton,
  SpecularLink,
} from '@/components/ui/specular-button/SpecularButton';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency } from '@/lib/format';
import './Welcome.css';

// Illustrative USD amounts, independent of the signed-in user's financial data.
const exampleCategories = [
  { name: 'Housing', amount: 1800, budget: 2000, width: 'w-[90%]' },
  { name: 'Groceries', amount: 640, budget: 800, width: 'w-[80%]' },
  { name: 'Transport', amount: 310, budget: 500, width: 'w-[62%]' },
  { name: 'Other spending', amount: 1100, budget: 1200, width: 'w-[92%]' },
];

function FinancialPreview() {
  const previewRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [replay, setReplay] = useState(0);
  const replayAnimation = () => setReplay(value => value + 1);

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(preview);
    return () => observer.disconnect();
  }, []);

  return (
    <figure
      ref={previewRef}
      id="showcase"
      className="financial-preview min-w-0 scroll-mt-24"
      data-visible={isVisible}
    >
      <div
        key={replay}
        className="financial-preview-frame rounded-lg border border-border bg-muted/60 p-3 sm:p-5"
      >
        <div className="rounded-md border border-border bg-card text-card-foreground shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-4 sm:px-6">
            <span className="text-sm font-semibold">Monthly overview</span>
            <span className="text-xs text-muted-foreground">June 2026</span>
          </div>

          <dl className="grid grid-cols-3 gap-2 border-b border-border px-5 py-6 sm:gap-4 sm:px-6">
            {[
              { label: 'Income', amount: 6400 },
              { label: 'Spending', amount: 3850 },
              { label: 'Money left', amount: 2550 },
            ].map(({ label, amount }) => (
              <div key={label} className="financial-preview-metric">
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-2 text-lg font-semibold tracking-tight tabular-nums sm:text-2xl">
                  {formatCurrency(amount, 0)}
                </dd>
              </div>
            ))}
          </dl>

          <div className="px-5 py-5 sm:px-6">
            <div className="mb-5 flex items-center justify-between gap-3 text-xs">
              <span className="font-medium">Spending by category</span>
              <span className="text-muted-foreground">Spent / budget</span>
            </div>
            <ul className="space-y-4">
              {exampleCategories.map(category => (
                <li key={category.name} className="financial-preview-category">
                  <div className="mb-2 flex items-baseline justify-between gap-3 text-xs">
                    <span>{category.name}</span>
                    <span className="whitespace-nowrap tabular-nums">
                      {formatCurrency(category.amount, 0)}
                      <span className="text-muted-foreground">
                        {' / '}
                        {formatCurrency(category.budget, 0)}
                      </span>
                    </span>
                  </div>
                  <div aria-hidden="true" className="h-1 rounded-full bg-muted">
                    <div
                      className={`financial-preview-bar h-full origin-left rounded-full bg-primary/65 ${category.width}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-border px-5 py-4 sm:px-6">
            <span className="text-xs text-muted-foreground">
              Remaining monthly budget
            </span>
            <span className="financial-preview-remaining text-sm font-semibold tabular-nums text-primary">
              {formatCurrency(650, 0)}
            </span>
          </div>
        </div>
      </div>
      <figcaption className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={replayAnimation}
          onFocus={event => {
            if (event.currentTarget.matches(':focus-visible'))
              replayAnimation();
          }}
          className="inline-flex items-center gap-1.5 rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background motion-reduce:hidden"
          aria-label="Replay overview animation"
        >
          <RotateCcw aria-hidden="true" className="h-3 w-3" />
          Replay
        </button>
      </figcaption>
    </figure>
  );
}

export function Welcome() {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleDemoLogin = async () => {
    try {
      setIsLoggingIn(true);
      setLoginError(null);
      await demoLogin();
      navigate('/dashboard');
    } catch (error) {
      setLoginError(
        error instanceof Error
          ? error.message
          : 'Unable to open the demo. Please try again.'
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="public-page relative isolate min-h-screen bg-background text-foreground">
      <Particles />
      <PublicHeader />

      <main id="main-content">
        <section
          aria-labelledby="welcome-heading"
          className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-14 sm:px-8 sm:py-20 lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:px-12 lg:py-24"
        >
          <div>
            <p className="mb-6 flex items-center gap-3 text-[10px] font-medium uppercase tracking-[0.18em] text-primary sm:text-xs">
              <span aria-hidden="true" className="h-px w-7 bg-primary" />
              Personal finance, in focus
            </p>
            <h1
              id="welcome-heading"
              className="max-w-lg font-serif text-[2.75rem] font-normal leading-[1.08] tracking-tight min-[375px]:text-5xl sm:text-6xl lg:text-[4rem]"
            >
              A clear view of
              <br className="hidden sm:block" /> your finances.
            </h1>
            <p className="mt-6 max-w-md text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              Track spending, budgets, and net worth in one place.
            </p>
            <div className="mt-9 flex flex-col gap-3 min-[400px]:flex-row">
              <SpecularLink to="/register">
                Create account
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </SpecularLink>
              <SpecularButton
                onClick={handleDemoLogin}
                disabled={isLoggingIn}
                aria-busy={isLoggingIn}
                aria-describedby={loginError ? 'demo-error' : undefined}
                className="specular-button--outline bg-background text-foreground hover:bg-muted"
              >
                {isLoggingIn && (
                  <Loader2
                    aria-hidden="true"
                    className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  />
                )}
                {isLoggingIn ? 'Opening demo…' : 'Explore demo'}
              </SpecularButton>
            </div>
            {loginError && (
              <p
                id="demo-error"
                role="alert"
                className="mt-4 max-w-md text-sm text-destructive"
              >
                {loginError}
              </p>
            )}
            <p className="mt-4 text-xs leading-5 text-muted-foreground">
              Sample data. No sign-up needed.
            </p>
          </div>
          <FinancialPreview />
        </section>
      </main>
    </div>
  );
}
